// Auth related store state and actions.

import axiosApiClient from '@/api/axiosApiClient'
import { getSavedState, saveState } from '@/store/helpers'
import i18next from '@/utils/i18next'
import log from '@/utils/log'
import Vue from 'vue'

// State
export const state = {
	// Get saved current user, if any.
	currentUser: getSavedState('auth.currentUser'),
	currentClient: getSavedState('auth.currentClient'),
	authError: null
}

// Mutations
export const mutations = {
	SET_CURRENT_USER(state, newValue) {
		state.currentUser = newValue // Update current user in store.
		saveState('auth.currentUser', newValue) // Save to storage.
		log.changeClientId(newValue ? newValue.id : null) // Change log context.
	},
	SET_CURRENT_CLIENT(state, newValue) {
		state.currentClient = newValue
		saveState('auth.currentClient', newValue)
	},
	SET_AUTH_ERROR(state, newValue) {
		state.authError = newValue
	}
}

// Getters
export const getters = {
	// Whether the user is currently logged in.
	loggedIn(state) {
		return !!state.currentUser
	},
	// include OPS also, isAdmin currently only use for report page
	isAdmin(state) {
		return state.currentUser
			? state.currentUser.roles.includes('ADM') ||
					state.currentUser.roles.includes('OPS')
			: false
	}
}

// Actions
export const actions = {
	// This is automatically run in `@/store/index.js` when the app starts,
	// along with any other actions named `init` in other modules.
	init({ state, commit }) {
		setResponseIncterceptors(state, commit)
	},

	// Submits email and request login link.
	async requestLoginLink(_, { email }) {
		try {
			const { protocol, host } = window.location
			const body = {
				confirmValue: email,
				confirmVia: ['EMAIL'],
				redirectURL: `${protocol}//${host}/#/verification`,
				locale: i18next.language
			}
			const { data } = await axiosApiClient.post('/v1/login', body)
			return data
		} catch (error) {
			throw constructApiError(error)
		}
	},

	// Submits login ID & code from login link.
	async verifyLoginLink({ commit }, { id, code }) {
		try {
			const response = await axiosApiClient.patch(`login/${id}`, { code })
			Vue.$cookies.set('auth_token', response.data.token, '30d')
			const { data } = await axiosApiClient.get(`users/self`)
			const emailAlias = data.aliases.find(
				alias => alias.type === 'EMAIL'
			)
			try {
				const { data: client } = await axiosApiClient.get(
					`clients/${data.clients[0]}`
				)
				commit('SET_CURRENT_CLIENT', client)
			} catch (e) {
				commit('SET_CURRENT_CLIENT', null)
				log.info('user does not have a client', e)
			}

			// Set current user data:
			const user = {
				id: data.id,
				clientId: data.clients[0] || null,
				name: data.info.name,
				roles: data.roles,
				email: emailAlias ? emailAlias.id : '',
				token: response.data.token
			}
			commit('SET_CURRENT_USER', user)
			return user
		} catch (error) {
			throw constructApiError(error)
		}
	},

	// Logs out the current user.
	logOut({ commit, dispatch }) {
		dispatch('placeOrder/clear', undefined, { root: true })
		dispatch(
			'spreadsheetSubmissions/resetSpreadsheetSubmissions',
			undefined,
			{ root: true }
		)
		commit('SET_CURRENT_USER', null)
		commit('SET_CURRENT_CLIENT', null)
		commit('SET_AUTH_ERROR', null)
		Vue.$cookies.remove('auth_token')
	}
}

// Set response interceptor.
function setResponseIncterceptors(state, commit) {
	axiosApiClient.interceptors.response.use(undefined, error => {
		const { response } = error
		if (response && (response.status === 401 || response.status === 403)) {
			commit('SET_AUTH_ERROR', error)
		}
		return Promise.reject(error)
	})
}

// Construct API error response message.
function constructApiError(error) {
	let errorMessage = error.message
	let requestId = ''
	let isApiError = !error.response
	if (error.response) {
		// Has response, get error response headers & data (request-id, code):
		const { data, headers, status } = error.response
		if (data && data.errors && data.errors.length) {
			const {
				code = '',
				detail = '',
				message = '',
				type = ''
			} = data.errors[0]
			errorMessage = `${code} ${detail} ${message}`
			isApiError = type !== 'client'
		}
		requestId = headers['x-request-id']
		isApiError = isApiError || status >= 500
	}
	return { message: errorMessage, requestId, isApiError }
}
